import { Box, Heading, useToast, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { useStep } from '@duke-hq/ui/components/StepWizard/useStep';
import { trpcAdmin } from '../../lib/trpcAdmin';
import { useTenantContext } from '../../TenantContext';
import { testIds } from '../testIds';
import { EmployerBusinessDetails } from './EmployerBusinessDetails';
import { EmployerPlacements } from './EmployerPlacements';
import { EmployerCourses } from './EmployerCourses';
import { buildPayload } from './payload';
import {
  emptyBusinessDetails,
  emptyPlacements,
  type BusinessDetails,
  type EmployerAddress,
  type PlacementsDetails,
} from './types';

// When `nominationId` is set the modal is promoting a reverse-marketing
// nomination ("Save to directory"): it submits via saveNominationToDirectory
// (create-or-match + link) rather than a plain create.
type ModalBodyProps = {
  onClose: () => void;
  initialBusiness?: BusinessDetails;
  initialAddress?: EmployerAddress | null;
  nominationId?: string;
};

function ModalBody({
  onClose,
  initialBusiness,
  initialAddress,
  nominationId,
}: ModalBodyProps): React.ReactElement {
  const { currentStep, goToNextStep, goToPrevStep, setStep } = useStep({
    maxStep: 2,
    initialStep: 0,
  });
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const ctx = useTenantContext();
  const googleApiKey = ctx?.apis.GOOGLE_ADDRESS_VALIDATION_API_KEY ?? '';

  const [business, setBusiness] = useState<BusinessDetails>(
    initialBusiness ?? emptyBusinessDetails,
  );
  const [address, setAddress] = useState<EmployerAddress | null>(initialAddress ?? null);
  const [placements, setPlacements] = useState<PlacementsDetails>(emptyPlacements);
  const [courseIds, setCourseIds] = useState<Set<string>>(new Set());

  const resetAndClose = () => {
    setBusiness(initialBusiness ?? emptyBusinessDetails);
    setAddress(initialAddress ?? null);
    setPlacements(emptyPlacements);
    setCourseIds(new Set());
    setStep(0);
    onClose();
  };

  const createEmployer = trpcAdmin.workPlacements.employers.createEmployer.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Employer added` });
      void utils.workPlacements.employers.listEmployers.invalidate();
      resetAndClose();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to add employer`, description: error.message });
    },
  });

  const saveToDirectory = trpcAdmin.workPlacements.employers.saveNominationToDirectory.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Saved to directory` });
      void utils.workPlacements.employers.listEmployers.invalidate();
      void utils.workPlacements.placements.listNominations.invalidate();
      resetAndClose();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to save to directory`, description: error.message });
    },
  });

  const isSaving = createEmployer.isPending || saveToDirectory.isPending;

  const onSave = () => {
    const payload = buildPayload(business, address, placements, courseIds);
    if (nominationId != null) {
      saveToDirectory.mutate({ ...payload, nominationId });
    } else {
      createEmployer.mutate(payload);
    }
  };

  return (
    <VStack w="full" h="full" align="stretch" spacing={0} data-testid={testIds.addEmployerModal}>
      <Box px={6} pt={6} pb={2}>
        <Heading size="tiny">
          <Trans>Add new employer listing</Trans>
        </Heading>
      </Box>

      {/* Kept mounted so the address form's selection survives Back navigation.
          Fills the modal as a column so its footer pins to the bottom, and
          scrolls internally if the tallest step outgrows the fixed height. */}
      <Box
        display={currentStep === 0 ? 'flex' : 'none'}
        flexDirection="column"
        flex="1"
        minH="0"
        overflowY="auto"
      >
        <EmployerBusinessDetails
          business={business}
          address={address}
          googleApiKey={googleApiKey}
          onNext={(nextBusiness, nextAddress) => {
            setBusiness(nextBusiness);
            setAddress(nextAddress);
            goToNextStep();
          }}
          onCancel={resetAndClose}
        />
      </Box>

      {currentStep === 1 ? (
        <Box display="flex" flexDirection="column" flex="1" minH="0" overflowY="auto">
          <EmployerPlacements
            placements={placements}
            setPlacements={setPlacements}
            goToPrevStep={goToPrevStep}
            goToNextStep={goToNextStep}
          />
        </Box>
      ) : null}

      {currentStep === 2 ? (
        <Box display="flex" flexDirection="column" flex="1" minH="0" overflowY="auto">
          <EmployerCourses
            courseIds={courseIds}
            setCourseIds={setCourseIds}
            goToPrevStep={goToPrevStep}
            onSave={onSave}
            isSaving={isSaving}
          />
        </Box>
      ) : null}
    </VStack>
  );
}

export function AddEmployerModal({
  isOpen,
  onClose,
  initialBusiness,
  initialAddress,
  nominationId,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialBusiness?: BusinessDetails;
  initialAddress?: EmployerAddress | null;
  nominationId?: string;
}): React.ReactElement {
  return (
    <FormModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      modalContentHeight={`min(90vh, ${pxToRem(800)})`}
      modalContent={
        <ModalBody
          onClose={onClose}
          initialBusiness={initialBusiness}
          initialAddress={initialAddress}
          nominationId={nominationId}
        />
      }
    />
  );
}
